import { useCallback, useContext, useState } from 'react';
import useCropSeasonServiceApi from 'hooks/useCropSeasonServiceApi';

import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import {
  CROP_SEASON_FETCH_V2,
  CROP_SEASON_FETCH_V2_BY_ORG,
  CROP_SEASON_UPDATE_V2,
  CROP_SEASON_UPDATE_V2_BY_ORG
} from 'constants/cropSeasonServiceV2Flags';

const useCropSeason = () => {
  const [{ cropSeasons }, dispatch] = useContext(Context);
  const [dividedCropSeasons, setDividedCropSeasons] = useState({
    active: [],
    upcoming: [],
    past: []
  });
  const fetchApi = useCropSeasonServiceApi(
    'cropseason',
    CROP_SEASON_FETCH_V2,
    CROP_SEASON_FETCH_V2_BY_ORG
  );
  const updateApi = useCropSeasonServiceApi(
    'cropseason',
    CROP_SEASON_UPDATE_V2,
    CROP_SEASON_UPDATE_V2_BY_ORG
  );

  const getCropSeason = useCallback(
    async id => {
      const { promise } = fetchApi.fetch(
        id,
        undefined,
        undefined,
        undefined,
        true // ignoreCache
      );
      return promise
        .then(({ data }) => {
          return data;
        })
        .catch(catchCancel)
        .catch(parseServerError(dispatch));
    },
    [dispatch, fetchApi]
  );

  const updateCropSeason = async (id, body) => {
    const { promise } = updateApi.update(id, body);
    return promise
      .then(({ data }) => {
        return data;
      })
      .catch(catchCancel)
      .catch(parseServerError(dispatch));
  };

  const splitCropSeasons = useCallback(() => {
    const currentTime = new Date().getTime();
    const groupedSeasons = { active: [], upcoming: [], past: [] };
    cropSeasons.forEach(item => {
      if (currentTime > item.startDateEpoch) {
        if (currentTime > item.endDateEpoch) {
          groupedSeasons.past.push(item);
        } else {
          groupedSeasons.active.push(item);
        }
      } else {
        groupedSeasons.upcoming.push(item);
      }
    });

    setDividedCropSeasons(groupedSeasons);
  }, [cropSeasons]);

  const prepareProperties = (cropZones, newFarmId, newCropzoneId) => {
    const farms = {};
    farms[newFarmId] = { farmId: newFarmId, cropzoneIds: [newCropzoneId] };

    cropZones.forEach(({ farmId, cropzoneId }) => {
      if (farms[farmId]) {
        if (!farms[farmId].cropzoneIds.find(id => id === cropzoneId)) {
          farms[farmId].cropzoneIds.push(cropzoneId);
        }
      } else {
        farms[farmId] = { farmId, cropzoneIds: [cropzoneId] };
      }
    });

    return Object.values(farms);
  };

  const removeProperties = (cropZones, oldFarmId, oldCropzoneId) => {
    const farms = {};
    farms[oldFarmId] = { farmId: oldFarmId, cropzoneIds: [] };

    cropZones.forEach(({ farmId, cropzoneId }) => {
      if (farms[farmId]) {
        if (cropzoneId !== oldCropzoneId) {
          farms[farmId].cropzoneIds.push(cropzoneId);
        }
      } else {
        farms[farmId] = { farmId, cropzoneIds: [cropzoneId] };
      }
    });

    return Object.values(farms).filter(property => property.cropzoneIds.length);
  };

  const prepareCropSeason = (
    cropSeason,
    farmId,
    cropzoneId,
    remove = false
  ) => {
    const {
      id,
      name,
      notes,
      startDateEpoch,
      endDateEpoch,
      cropZones
    } = cropSeason;

    const properties = !remove
      ? prepareProperties(cropZones, farmId, cropzoneId)
      : removeProperties(cropZones, farmId, cropzoneId);

    return {
      id,
      name,
      notes,
      properties,
      endDateEpoch,
      startDateEpoch,
      createdDate: cropSeason.createdDate,
      createdDateEpoch: cropSeason.createdDateEpoch
    };
  };

  return {
    getCropSeason,
    updateCropSeason,
    dividedCropSeasons,
    splitCropSeasons,
    prepareCropSeason
  };
};

export default useCropSeason;
